import { FC, ReactNode } from 'react'
import Popover from '@/components/UiElements/PopOver'
import Alert from '@/components/UiElements/Alert'
import ImageComponent from '@/components/UiElements/ImageComponent'
import { FlattedDesignTemplate } from '@/types/designTemplate'

type DesignTemplateItemProps = {
  template: FlattedDesignTemplate
  className?: string
  onDelete?: (id: string) => void
  onToggleActive?: (template: FlattedDesignTemplate) => void
  onDesign?: (template: FlattedDesignTemplate) => void
  /** Shown on the row while its design is being fetched. */
  busy?: boolean
  callBack: (template: FlattedDesignTemplate) => void
}

const Label = ({
  title,
  value,
  enable = true,
  className,
}: {
  title: string | ReactNode
  value: string | ReactNode
  enable?: boolean
  className?: string | null
}) => {
  return (
    <div
      className={`py-sm px-md h-[50px] rounded-md transition-all ${
        enable ? 'bg-gray-100 group-hover:bg-gray-200 ' : 'bg-gray-50 group-hover:bg-gray-100'
      } ${enable ? 'text-gray-900 group-hover:text-gray-900' : 'text-gray-400 group-hover:text-gray-900'} ${
        className || ''
      }`}
    >
      <p className={'text-gray-400 text-xs'}>{title}</p>
      <p className={'text-sm'}>{value}</p>
    </div>
  )
}

const DesignTemplateItems: FC<DesignTemplateItemProps> = ({
  template,
  className = '',
  onDelete,
  onToggleActive,
  onDesign,
  busy = false,
  callBack,
}) => {
  const handleDeleteItem = () => {
    Alert.showAlert({
      title: 'Delete design template',
      isCancelable: true,
      danger: true,
      button: 'Delete!',
      message: (
        <div className={'flex flex-col items-center justify-center'}>
          Are you sure you want to delete the template &quot;{template.name}&quot;?
        </div>
      ),
      callBack: async (ok) => {
        if (ok) onDelete?.(template.id)
      },
    })
  }

  const availability = template.isGeneric
    ? 'Every section'
    : template.sections.length
    ? `${template.sections.length} section${template.sections.length > 1 ? 's' : ''}`
    : 'No sections yet'

  return (
    <div
      onClick={() => callBack(template)}
      id={template.id}
      className={`relative group active:bg-gray-100 flex py-sm border-b-gray-200 border-b-[1px] items-center hover:bg-gray-50 cursor-pointer transition-all gap-md px-md h-[140px] ${
        !template.isActive ? 'opacity-60' : ''
      } ${className}`}
    >
      <Popover
        toggle={(open) => (
          <div
            className={`select-none w-[37px] h-[37px] min-w-[37px] ${
              open ? 'text-white bg-gray-900' : 'bg-gray-200 hover:bg-gray-700 text-gray-900 hover:text-white'
            } rounded-full flex-col justify-center items-center inline-flex font-icon-regular cursor-pointer transition-all`}
          >
            <div className="text-center">ellipsis</div>
          </div>
        )}
        placement="bottom left"
        offsetX={0}
        offsetY={10}
        menu={[
          {
            title: <span className="capitalize">Edit template</span>,
            icon: '',
            onClick: () => callBack(template),
          },
          {
            title: <span className="capitalize">Design template</span>,
            icon: '',
            onClick: () => onDesign?.(template),
          },
          {
            title: <span className="capitalize">{template.isActive ? 'Deactivate' : 'Activate'}</span>,
            icon: '',
            onClick: () => onToggleActive?.(template),
          },
          { separator: true, className: 'my-[10px]' },
          {
            title: <span className="capitalize">Delete</span>,
            icon: '',
            className: 'hover:bg-red-100 text-red-500',
            onClick: handleDeleteItem,
          },
        ]}
      />

      <div className="flex flex-row gap-md items-center w-full">
        <div className="w-[100px] h-[100px] min-w-[100px] rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
          {template.thumbnail ? (
            <ImageComponent src={template.thumbnail} className="!w-[90px] !h-[90px]" />
          ) : (
            <div className="w-[90px] h-[90px] flex flex-col items-center justify-center text-gray-300">
              <span className="font-icon-regular text-[32px]">image</span>
              <span className="text-xs mt-xs">No preview</span>
            </div>
          )}
        </div>

        <div className="flex flex-col justify-between gap-md items-start transition-all h-[100px] w-full">
          <div className="text-title-md pl-md transition-all flex flex-row gap-sm items-center">
            <span className="capitalize">{template.name}</span>
            {busy && <span className="text-xs text-gray-400 normal-case">Opening...</span>}
            {!template.isActive && (
              <span className="text-xs px-sm rounded-sm bg-gray-200 text-gray-600 normal-case">Inactive</span>
            )}
          </div>

          <div className="flex flex-row gap-sm justify-between w-full">
            <div className="flex flex-row gap-sm">
              <Label title="Available on" value={availability} className="w-[160px]" />
              <Label
                title="Customer fills in"
                value={
                  template.placeholderCount
                    ? `${template.placeholderCount} field${template.placeholderCount > 1 ? 's' : ''}`
                    : 'Nothing yet'
                }
                enable={template.placeholderCount > 0}
                className="w-[150px]"
              />
              <Label
                title="Design"
                value={
                  template.elementCount
                    ? `${template.elementCount} element${template.elementCount > 1 ? 's' : ''}`
                    : 'Not designed'
                }
                enable={template.elementCount > 0}
                className="w-[140px]"
              />
              <Label
                title="Drawn on"
                value={template.origin?.sku || 'Not chosen'}
                enable={!!template.origin?.sku}
                className="w-[140px]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DesignTemplateItems
